import React, { FC, memo } from "react";
import ResponsiveTable from "./ResponsiveTable";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleNotch } from "@fortawesome/free-solid-svg-icons";
import { currencyFormatter, yyyyddmmTommddyyyy } from "../../common/utility";
import { MemberClaim } from "types/MemberClaim";
import { Grid, Stack, Typography } from "@mui/material";
import { useClaimServiceLineData } from "hooks/useClaimServiceLineData";
import { ExtendedMemberClaim } from "types/ExtendedMemberClaim";

export type SelectedClaimDetialProps = {
  claim: ExtendedMemberClaim;
};
const SelectedClaimDetail: FC<SelectedClaimDetialProps> = memo(({ claim }) => {
  const format = currencyFormatter.format;

  const {
    data: claimServiceLines = [],
    isLoading: isLoadingClaimServiceLines,
  } = useClaimServiceLineData(claim.claim_number);

  return (
    <Stack spacing={4} py={2}>
      <Typography variant="h3" component="h2" fontWeight="bold">
        {claim.claim_number} - Claim Details
      </Typography>
      <Grid container spacing={2} mb={2}>
        <Grid size={6}>
          <Grid container spacing={2}>
            <Grid size={4}>
              <Typography fontWeight="bold">Claim Type:</Typography>
            </Grid>
            <Grid size={8}>
              <Typography>{claim.claim_type}</Typography>
            </Grid>
            <Grid size={4}>
              <Typography fontWeight="bold">Service Provider:</Typography>
            </Grid>
            <Grid size={8}>
              <Typography>{claim.provider_name}</Typography>
            </Grid>
            <Grid size={4}>
              <Typography fontWeight="bold">Service Date:</Typography>
            </Grid>
            <Grid size={8}>
              <Typography>{claim.service_date}</Typography>
            </Grid>
          </Grid>
        </Grid>
        <Grid size={6}>
          <Grid container spacing={2}>
            <Grid size={6}>
              <Typography fontWeight="bold">
                Amount the provider billed:
              </Typography>
            </Grid>
            <Grid size={6}>
              <Typography>
                {format(claim.claim_detail?.total_billed_amount)}
              </Typography>
            </Grid>
            <Grid size={6}>
              <Typography fontWeight="bold">
                Total Cost (amount the plan approved):
              </Typography>
            </Grid>
            <Grid size={6}>
              <Typography>
                {format(claim.claim_detail?.total_allowed_amount)}
              </Typography>
            </Grid>
            <Grid size={6}>
              <Typography fontWeight="bold">Plan&apos;s Share</Typography>
            </Grid>
            <Grid size={6}>
              <Typography>
                {format(claim.claim_detail.total_paid_amount)}
              </Typography>
            </Grid>
            <Grid size={6}>
              <Typography fontWeight="bold">
                Your Share (your provider may bill you):
              </Typography>
            </Grid>
            <Grid size={6}>
              <Typography>
                {" "}
                {format(claim.claim_detail.total_patient_responsibility_amount)}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {isLoadingClaimServiceLines ? (
        <Stack justifyContent="center" alignItems="center" my={8}>
          <FontAwesomeIcon
            icon={faCircleNotch}
            aria-label="Currently Loading Content"
            size="3x"
          />
        </Stack>
      ) : (
        <ResponsiveTable
          isFirstTimeLoading={isLoadingClaimServiceLines}
          tableOptions={{
            data: claimServiceLines.map((line) => ({
              ...line,
              service_from_date: yyyyddmmTommddyyyy(line.service_from_date),
              service_to_date: yyyyddmmTommddyyyy(line.service_to_date),
            })),
            columns: [
              {
                accessorFn: (row) =>  row.procedure?.[0]?.procedure_code,
                header: "Service Code",
                 meta: { width: "10%" },
              },
              {
                accessorFn: (row) => row.procedure?.[0]?.procedure_description,
                header: "Service Description",
                meta: { width: "25%" },
              },
              {
                accessorKey: "service_from_date",
                header: "Start Date",
                meta: { width: "10%" },
              },
              {
                accessorKey: "service_to_date",
                header: "End Date",
                meta: { width: "10%" },
              },
              {
                accessorKey: "billed_amount",
                header: "Amount the provider billed",
                meta: { width: "10%" },
                cell: (info) => format(info.getValue<number>()),
              },
              {
                accessorKey: "allowed_amount",
                header: "Total Cost (amount the plan approved)",
                meta: { width: "10%" },
                cell: (info) => format(info.getValue<number>()),
              },
              {
                accessorKey: "paid_amount",
                header: `Plan's share`,
                meta: { width: "10%" },
                cell: (info) => format(info.getValue<number>()),
              }
            ]
          }}
        />
      )}
    </Stack>
  );
});
SelectedClaimDetail.displayName = "SelectedClaimDetial";
export default SelectedClaimDetail;
